'use client';
import HTMLReactParser from 'html-react-parser';
import { useLocale } from 'next-intl';
import qs from 'qs';
import { useState, useEffect, ReactNode } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { z } from 'zod';

import Rodo from '../../contact/components/Rodo';
import { fetchUser } from '../../profile/lib/user';

import Agreement from './Agreement';

import { pushFBPixelEvent } from '@components/Analytics/Facebook/FBPixelEvent';
import { pushGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, Checkbox, Input } from '@components/Form';
import Loader from '@components/root/Loader';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { IPRESSO_SOURCES } from '@utils/api/client/ipresso';
import {
  IpressoFetchResponse,
  IpressoPostBody,
} from '@utils/api/server/ipresso';
import { newslettersByCountries } from '@utils/newsletter';
import { useUserStore } from '@utils/user';
import { langs } from 'types/lang';
import { User } from 'types/user';
import { getIPressoAgreements } from 'utils/ipresso';
interface Props {
  urlEmail: string;
  labels: {
    topic: string;
    newsletterTo: string;
    email: string;
    subscribe: string;
    rodoMinimized: string;
    marketingTitle: string;
    marketingContent: string;
    commercialInsideTitle: string;
    commercialInsideContent: string;
    commercialOutsideTitle: string;
    commercialOutsideContent: string;
    rodoTooltip: ReactNode;
    rodoTitle: string;
    rodoContent: ReactNode;
    consentNewsletter: ReactNode;
    germanRules: string;
    newsletterTooltip: string;
    title: {
      driver: string;
      logistician: string;
      carrier: string;
      forwarder: string;
      shipper: string;
    };
    error: {
      moreThanOneCategory: string;
      email: string;
      saving: string;
    };
    success: {
      saving: string;
    };
  };
}

const CategoryForm = ({ labels, urlEmail = '' }: Props) => {
  const userStore = useUserStore();

  const locale = useLocale();
  const [checked, setChecked] = useState<any[]>([]);
  const [hover, setHover] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [user, setUser] = useState<User | null>();

  const [email, setEmail] = useState(urlEmail);

  const [marketingAgreement, setMarketingAgreement] = useState(false);
  const [commercialInside, setCommercialInside] = useState(false);
  const [commercialOutside, setCommercialOutside] = useState(false);
  const [germanRulesConsent, setGermanRulesConsent] = useState(false);

  useEffect(() => {
    if (!userStore.isReady || !userStore.accessToken || !userStore.isLoggedIn) {
      return;
    }

    fetchUser(setUser, userStore.accessToken);
  }, [userStore.isReady]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (checked.length === 0) {
        if (!user) {
          return;
        }

        const params = {
          email: user.email,
          lang: locale,
        };
        const query = qs.stringify(params);

        const response = await fetchApi<IpressoFetchResponse>(
          `/campaign/fetch?${query}`,
          {
            method: 'GET',
            noAuth: true,
          },
        );

        const resData = response.data.data;

        if (resData) {
          setChecked(Object.keys(resData.categoryData));
          setMarketingAgreement(resData.agreementData.marketing ?? false);
        }
      }
    };

    if (user?.email) {
      try {
        fetchCategories();
        setEmail(user.email);
        setMarketingConsent(user?.marketingConsent ?? false);
        return;
      } catch (e) {
        // eslint-disable-next-line no-console
        logger.error(e, 'Contact page %s', (e as any)?.message);
      }
    }
    if (urlEmail.length > 0) {
      setEmail(urlEmail.replace(/ /g, '+'));
    }
  }, [user, urlEmail]);

  if (!Object.keys(newslettersByCountries).includes(locale))
    return <div>{'Newsletters aren\'t available in your country!'}</div>;

  const onFormSend = async (e: any) => {
    e.preventDefault();
    const inputEmail = email || e.target?.email?.value;
    const isEmailValid =
      inputEmail === undefined
        ? true
        : z.string().email().safeParse(inputEmail).success;

    if (checked.length === 0) {
      toastError(labels.error.moreThanOneCategory);
      return;
    }

    if (!isEmailValid) {
      toastError(labels.error.email);
      return;
    }

    try {
      const categories = newslettersByCountries[locale].reduce(
        (acc, categoryName) => {
          const restCategoryName = categoryName;
          return {
            ...acc,
            [restCategoryName]: checked.includes(categoryName),
          };
        },
        {},
      );
      const categoryNames = Object.keys(categories).filter(key => categories[key] === true).join(' ');

      const bodyObject: IpressoPostBody = {
        contactData: { email: user?.email || inputEmail },
        categoryData: categories,
        agreementData: {},
        lang: { category: locale },
        source: IPRESSO_SOURCES.NEWSLETTER,
      };

      if (locale !== 'de') {
        const agreements = getIPressoAgreements({
          marketingAgreement,
          commercialInside,
          commercialOutside,
        });
        bodyObject.agreementData = agreements;
        bodyObject.lang.agreement = locale;
      }

      if (user?.id) {
        bodyObject.userId = user.id.toString();
      }

      //? Ipresso Agreements
      await fetchApi<IpressoFetchResponse>('/campaign/edit', {
        method: 'POST',
        noAuth: true,
        body: JSON.stringify(bodyObject),
      });

      const userInfo = userStore.getUserInfoForAnalytics();
      pushFBPixelEvent('Lead',
        {
          ...userInfo,
          content_name: 'newsletter-subscibed',
          transinfo_newsletter_email_subscribed: inputEmail,
          transinfo_newsletter_lang: locale,
          transinfo_newsletter_categories: categoryNames
        }
      );
      pushGAEvent(userStore, 'transinfo_newsletter_subscibed', locale, {
        transinfo_view_category: categoryNames,
        transinfo_newsletter_email: inputEmail
      });

      toastSuccess(labels.success.saving);
    } catch (e) {
      logger.error(e, 'Contact page %s', (e as any)?.message);
      toastError(labels.error.saving);
    }
  };

  if (!user && userStore.isLoggedIn) {
    return <Loader />;
  }

  return (
    <div className="category-tile">
      <div className="mb-1">{labels.topic}</div>
      <form action="" onSubmit={(e) => onFormSend(e)}>
        <div className="category-tile_wrapper">
          {newslettersByCountries[locale].map((category: string) => (
            <div
              className="category-tile_box mb-1"
              onMouseOver={() => setHover(category)}
              onMouseOut={() => setHover('')}
              key={`category.${category}`}
            >
              <Checkbox
                className="category-checkbox"
                checked={!!checked.includes(category)}
                label={labels.title[category]}
                onChange={() => {
                  if (checked.includes(category)) {
                    setChecked(
                      checked.filter(
                        (checkedCategory) => checkedCategory !== category,
                      ),
                    );
                  } else {
                    setChecked(checked.concat([category]));
                  }
                }}
              />
            </div>
          ))}
        </div>
        <div className="category-info">
          {hover !== '' && (
            <div key={'name.' + hover}>{labels.title[hover]}</div>
          )}
        </div>
        <div>
          <h6 className="my-2">
            {labels.newsletterTo} <span className="font-bold">{email}</span>
          </h6>
          {urlEmail.length === 0 && user?.email === undefined && (
            <div className="form-line big w-100 w-full gap-2">
              <Input
                defaultValue={email}
                placeholder={labels.email}
                name="email"
                data-testid="input.email"
                required
              />

              <Button>{labels.subscribe}</Button>
            </div>
          )}
        </div>
        {locale === langs.ua && (
          <>
            <div
              className="rodo"
              data-place="top"
              data-type="light"
              data-effect="solid"
              data-class="contact__tooltip"
              data-tip
              data-for="rodo"
            >
              {labels.rodoMinimized}
            </div>
            <ReactTooltip id="rodo" aria-haspopup="true">
              {labels.rodoTooltip}
            </ReactTooltip>
          </>
        )}
        <div className="form-flex mt-5">
          {locale !== 'de' ? (
            <>
              <Agreement
                checkboxState={marketingAgreement}
                setCheckboxState={setMarketingAgreement}
                title={labels.marketingTitle}
                content={labels.marketingContent}
                required
              />
              <Agreement
                checkboxState={commercialInside}
                setCheckboxState={setCommercialInside}
                title={labels.commercialInsideTitle}
                content={labels.commercialInsideContent}
              />
              <Agreement
                checkboxState={commercialOutside}
                setCheckboxState={setCommercialOutside}
                title={labels.commercialOutsideTitle}
                content={labels.commercialOutsideContent}
              />
              <Rodo title={labels.rodoTitle} content={labels.rodoContent} />
            </>
          ) : (
            <>
              <Checkbox
                checked={marketingConsent}
                className="mb-2"
                onChange={() => setMarketingConsent(!marketingConsent)}
                required
                label={
                  <div
                    data-place="top"
                    data-type="light"
                    data-effect="solid"
                    data-class="contact__tooltip"
                    data-tip
                    data-for="consent"
                  >
                    {labels.consentNewsletter}
                  </div>
                }
              />
              <Checkbox
                checked={germanRulesConsent}
                onChange={() => setGermanRulesConsent((prev) => !prev)}
                className="mb-2"
                label={HTMLReactParser(labels.germanRules)}
                required
              />
            </>
          )}
        </div>
        {locale === langs.ua && (
          <ReactTooltip id="consent">{labels.newsletterTooltip}</ReactTooltip>
        )}
        {(urlEmail.length !== 0 || user?.email !== undefined) && (
          <Button>{labels.subscribe}</Button>
        )}
      </form>
    </div>
  );
};

export default CategoryForm;
